import React, { createContext, useContext, useReducer, useCallback } from 'react';

const AppContext = createContext();

const DEFAULT_USERS = [
  {
    id: 'user-1',
    name: '我',
    bio: '这是我的朋友圈 ✨',
    avatar: null,
    following: ['user-2', 'user-3'],
    followers: ['user-2'],
  },
  {
    id: 'user-2',
    name: '小明',
    bio: '热爱生活，热爱代码',
    avatar: null,
    following: ['user-1'],
    followers: ['user-1', 'user-3'],
  },
  {
    id: 'user-3',
    name: '小红',
    bio: '旅行 | 摄影 | 美食',
    avatar: null,
    following: ['user-2'],
    followers: ['user-1'],
  },
];

const DEFAULT_POSTS = [
  {
    id: 'post-1',
    userId: 'user-2',
    content: '今天天气真好，出去散步了 🌤️',
    images: [],
    likes: ['user-1'],
    comments: [
      { id: 'c1', userId: 'user-1', text: '天气确实不错！', time: Date.now() - 3600000 },
    ],
    createdAt: Date.now() - 7200000,
  },
  {
    id: 'post-2',
    userId: 'user-3',
    content: '分享一下最近做的甜品 🍰',
    images: [],
    likes: ['user-1', 'user-2'],
    comments: [],
    createdAt: Date.now() - 86400000,
  },
  {
    id: 'post-3',
    userId: 'user-1',
    content: '学习 React 中，感觉收获满满！💪',
    images: [],
    likes: ['user-2', 'user-3'],
    comments: [
      { id: 'c2', userId: 'user-2', text: '加油！React 很棒的', time: Date.now() - 1800000 },
      { id: 'c3', userId: 'user-3', text: '一起学习！', time: Date.now() - 900000 },
    ],
    createdAt: Date.now() - 3600000,
  },
];

function reducer(state, action) {
  switch (action.type) {
    case 'ADD_POST': {
      const post = {
        id: 'post-' + Date.now(),
        userId: state.currentUserId,
        content: action.payload.content,
        images: action.payload.images || [],
        likes: [],
        comments: [],
        createdAt: Date.now(),
      };
      return { ...state, posts: [post, ...state.posts] };
    }
    case 'DELETE_POST':
      return { ...state, posts: state.posts.filter((p) => p.id !== action.payload) };
    case 'TOGGLE_LIKE': {
      const uid = state.currentUserId;
      return {
        ...state,
        posts: state.posts.map((p) => {
          if (p.id !== action.payload) return p;
          const liked = p.likes.includes(uid);
          return { ...p, likes: liked ? p.likes.filter((id) => id !== uid) : [...p.likes, uid] };
        }),
      };
    }
    case 'ADD_COMMENT': {
      const { postId, text } = action.payload;
      const comment = { id: 'c-' + Date.now(), userId: state.currentUserId, text, time: Date.now() };
      return {
        ...state,
        posts: state.posts.map((p) =>
          p.id === postId ? { ...p, comments: [...p.comments, comment] } : p
        ),
      };
    }
    case 'UPDATE_PROFILE': {
      const { name, bio, avatar } = action.payload;
      return {
        ...state,
        users: state.users.map((u) =>
          u.id === state.currentUserId
            ? { ...u, ...(name !== undefined && { name }), ...(bio !== undefined && { bio }), ...(avatar !== undefined && { avatar }) }
            : u
        ),
      };
    }
    case 'TOGGLE_FOLLOW': {
      const targetId = action.payload;
      const myId = state.currentUserId;
      const me = state.users.find((u) => u.id === myId);
      const isFollowing = me.following.includes(targetId);
      return {
        ...state,
        users: state.users.map((u) => {
          if (u.id === myId) {
            return {
              ...u,
              following: isFollowing
                ? u.following.filter((id) => id !== targetId)
                : [...u.following, targetId],
            };
          }
          if (u.id === targetId) {
            return {
              ...u,
              followers: isFollowing
                ? u.followers.filter((id) => id !== myId)
                : [...u.followers, myId],
            };
          }
          return u;
        }),
      };
    }
    case 'SET_VIEW':
      return { ...state, view: action.payload.view, viewUserId: action.payload.userId || null };
    default:
      return state;
  }
}

const initialState = {
  currentUserId: 'user-1',
  users: DEFAULT_USERS,
  posts: DEFAULT_POSTS,
  view: 'feed',
  viewUserId: null,
};

export function AppProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  const getUser = useCallback((id) => state.users.find((u) => u.id === id), [state.users]);
  const currentUser = getUser(state.currentUserId);

  return (
    <AppContext.Provider value={{ state, dispatch, getUser, currentUser }}>
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  return useContext(AppContext);
}
